import { Box, Paper, Tab, Tabs, TextField, Typography } from '@mui/material'
import { RetailerAccount } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import React from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router'

import LinkChild from '@src/lib/LinkChild'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import RetailerSettingsBrandSettingsAutomations from './RetailerSettingsBrandSettingsAutomations'
import RetailerSettingsBrandSettingsLocations from './RetailerSettingsBrandSettingsLocations'
import RetailerSettingsBrandSettingsQuestions from './RetailerSettingsBrandSettingsQuestions'
import RetailerSettingsBrandSettingsWebsite from './RetailerSettingsBrandSettingsWebsite'
import retailerSettingsPaths from './retailerSettingsPaths'

const RetailerSettingsBrandSettingsLayout: React.FC = () => {
  const params = retailerSettingsPaths.brandSettings.useParams()
  const brandSlug = params.brandSlug
  const currentRetailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )
  const history = useHistory()

  let selectedTab = ''
  if (retailerSettingsPaths.brandSettingsAutomations.match()) {
    selectedTab = 'brandSettingsAutomations'
  } else if (retailerSettingsPaths.brandSettingsLocations.match()) {
    selectedTab = 'brandSettingsLocations'
  } else if (retailerSettingsPaths.brandSettingsQuestions.match()) {
    selectedTab = 'brandSettingsQuestions'
  } else if (retailerSettingsPaths.brandSettingsWebsite.match()) {
    selectedTab = 'brandSettingsWebsite'
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {I18n.t('retailerSettings.brandSettings.title')}
      </Typography>

      <TextField
        select
        value={brandSlug || '-1'}
        fullWidth
        margin="none"
        sx={{ marginBottom: 2 }}
        onChange={(event) => {
          let newLocation: string | undefined
          const newBrandSlug = event.target.value

          if (newBrandSlug === '-1') {
            newLocation = retailerSettingsPaths.brandSettings.build({
              retailerAccountSlug: params.retailerAccountSlug,
            })
          } else {
            switch (selectedTab) {
              case 'brandSettingsAutomations':
              case '':
                newLocation =
                  retailerSettingsPaths.brandSettingsAutomations.build({
                    retailerAccountSlug: params.retailerAccountSlug,
                    brandSlug: newBrandSlug,
                  })
                break
              case 'brandSettingsLocations':
                newLocation =
                  retailerSettingsPaths.brandSettingsLocations.build({
                    retailerAccountSlug: params.retailerAccountSlug,
                    brandSlug: newBrandSlug,
                  })
                break
              case 'brandSettingsWebsite':
                newLocation = retailerSettingsPaths.brandSettingsWebsite.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  brandSlug: newBrandSlug,
                })
                break
              case 'brandSettingsQuestions':
                newLocation =
                  retailerSettingsPaths.brandSettingsQuestions.build({
                    retailerAccountSlug: params.retailerAccountSlug,
                    brandSlug: newBrandSlug,
                  })
                break
            }
          }

          if (newLocation) {
            history.push(newLocation)
          }
        }}
      >
        <MenuItemWithCheckbox value={'-1'}>
          <Typography color="text.secondary">Choose a brand ...</Typography>
        </MenuItemWithCheckbox>

        {currentRetailerAccount.retailers?.map((x) => {
          return (
            <MenuItemWithCheckbox key={x.id} value={x.brand?.slug || ''}>
              {x.brand?.name}
            </MenuItemWithCheckbox>
          )
        })}
      </TextField>

      {params.brandSlug ? (
        <Paper>
          <Tabs value={selectedTab}>
            <LinkChild
              to={retailerSettingsPaths.brandSettingsAutomations.build({
                retailerAccountSlug: params.retailerAccountSlug,
                brandSlug: params.brandSlug,
              })}
              value="brandSettingsAutomations"
            >
              <Tab
                label={I18n.t(
                  'retailerSettings.brandSettings.automations.title',
                )}
              />
            </LinkChild>

            <LinkChild
              to={retailerSettingsPaths.brandSettingsLocations.build({
                retailerAccountSlug: params.retailerAccountSlug,
                brandSlug: params.brandSlug,
              })}
              value="brandSettingsLocations"
            >
              <Tab
                label={I18n.t('retailerSettings.brandSettings.locations.title')}
              />
            </LinkChild>

            <LinkChild
              to={retailerSettingsPaths.brandSettingsWebsite.build({
                retailerAccountSlug: params.retailerAccountSlug,
                brandSlug: params.brandSlug,
              })}
              value="brandSettingsWebsite"
            >
              <Tab
                label={I18n.t('retailerSettings.brandSettings.website.title')}
              />
            </LinkChild>

            <LinkChild
              to={retailerSettingsPaths.brandSettingsQuestions.build({
                retailerAccountSlug: params.retailerAccountSlug,
                brandSlug: params.brandSlug,
              })}
              value="brandSettingsQuestions"
            >
              <Tab
                label={I18n.t('retailerSettings.brandSettings.questions.title')}
              />
            </LinkChild>
          </Tabs>

          <Box sx={{ padding: 2 }}>
            <Switch>
              <Route
                path={retailerSettingsPaths.brandSettingsAutomations.path}
                component={RetailerSettingsBrandSettingsAutomations}
              />
              <Route
                path={retailerSettingsPaths.brandSettingsLocations.path}
                component={RetailerSettingsBrandSettingsLocations}
              />
              <Route
                path={retailerSettingsPaths.brandSettingsWebsite.path}
                component={RetailerSettingsBrandSettingsWebsite}
              />
              <Route
                path={retailerSettingsPaths.brandSettingsQuestions.path}
                component={RetailerSettingsBrandSettingsQuestions}
              />
            </Switch>
          </Box>
        </Paper>
      ) : undefined}
    </>
  )
}

export default RetailerSettingsBrandSettingsLayout
